import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Building2, DollarSign, TrendingUp, Globe,
  Percent, Award, AlertCircle, CheckCircle,
  Info, Search, Loader, X
} from 'lucide-react';

const ETFXRay = () => {
  const [symbol, setSymbol] = useState('');
  const [searchSymbol, setSearchSymbol] = useState('');
  const [etfData, setEtfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // ✅ POPULAR ETFs FOR AUTOCOMPLETE
  const popularETFs = [
    { symbol: 'SPY', name: 'SPDR S&P 500 ETF Trust' },
    { symbol: 'QQQ', name: 'Invesco QQQ Trust' },
    { symbol: 'VOO', name: 'Vanguard S&P 500 ETF' },
    { symbol: 'IVV', name: 'iShares Core S&P 500 ETF' },
    { symbol: 'VTI', name: 'Vanguard Total Stock Market ETF' },
    { symbol: 'AGG', name: 'iShares Core US Aggregate Bond ETF' },
    { symbol: 'BND', name: 'Vanguard Total Bond Market ETF' },
    { symbol: 'VEA', name: 'Vanguard FTSE Developed Markets ETF' },
    { symbol: 'IEFA', name: 'iShares Core MSCI EAFE ETF' },
    { symbol: 'VWO', name: 'Vanguard FTSE Emerging Markets ETF' },
    { symbol: 'EEM', name: 'iShares MSCI Emerging Markets ETF' },
    { symbol: 'VNQ', name: 'Vanguard Real Estate ETF' },
    { symbol: 'GLD', name: 'SPDR Gold Trust' },
    { symbol: 'SLV', name: 'iShares Silver Trust' },
    { symbol: 'TLT', name: 'iShares 20+ Year Treasury Bond ETF' },
    { symbol: 'IWM', name: 'iShares Russell 2000 ETF' },
    { symbol: 'XLF', name: 'Financial Select Sector SPDR Fund' },
    { symbol: 'XLE', name: 'Energy Select Sector SPDR Fund' },
    { symbol: 'XLK', name: 'Technology Select Sector SPDR Fund' },
    { symbol: 'XLV', name: 'Health Care Select Sector SPDR Fund' }
  ];

  // ✅ FILTER SUGGESTIONS AS USER TYPES
  useEffect(() => {
    if (searchSymbol.trim().length > 0) {
      const filtered = popularETFs.filter(etf =>
        etf.symbol.toLowerCase().includes(searchSymbol.toLowerCase()) ||
        etf.name.toLowerCase().includes(searchSymbol.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchSymbol]);

  const selectSuggestion = (etf) => {
    setSearchSymbol(etf.symbol);
    setShowSuggestions(false);
    searchETF(etf.symbol);
  };

  const searchETF = async (symbolToSearch = searchSymbol) => {
    if (!symbolToSearch.trim()) return;

    setLoading(true);
    setError(null);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${API_URL}/etf/profile/${symbolToSearch}`);
      const data = await response.json();

      console.log('ETF X-Ray API Response:', data); // Debug log

      if (data.success) {
        setEtfData(data.data);
        setSymbol(symbolToSearch.toUpperCase());
      } else {
        setError(data.error || 'ETF not found');
      }
    } catch (err) {
      console.error('Error fetching ETF:', err);
      setError('Failed to fetch ETF data');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchETF();
    }
  };

  // COLORS for charts
  const SECTOR_COLORS = [
    '#60a5fa', '#34d399', '#fbbf24', '#f87171',
    '#a78bfa', '#fb923c', '#38bdf8', '#4ade80'
  ];

  const GEO_COLORS = [
    '#8b5cf6', '#ec4899', '#10b981', '#f59e0b',
    '#06b6d4', '#84cc16', '#f43f5e', '#6366f1'
  ];

  // ✅ HELPER: Format display value - remove duplicate % signs
  const formatDisplayValue = (value, suffix = '') => {
    if (!value || value === 'N/A') return 'N/A';
    
    // If value already contains the suffix (like %), don't add it again
    const strValue = String(value);
    if (suffix === '%' && strValue.includes('%')) {
      return strValue;
    }
    if (suffix === '$' && strValue.includes('$')) {
      return strValue;
    }
    
    return `${strValue}${suffix}`;
  };

  // ✅ HELPER: Format AUM value
  const formatAUM = (aum) => {
    if (!aum) return 'N/A';
    
    // If already formatted (contains $, B, T, M)
    const strAum = String(aum);
    if (strAum.includes('$') || strAum.includes('B') || strAum.includes('T') || strAum.includes('M')) {
      return strAum.startsWith('$') ? strAum : `$${strAum}`;
    }
    
    // Format raw number
    const num = parseFloat(aum);
    if (isNaN(num)) return 'N/A';
    
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  // ✅ HELPER: Get raw expense ratio for calculations
  const getExpenseRatioRaw = () => {
    if (!etfData) return 0;
    
    // Try expense_ratio_raw first, then parse from expense_ratio
    if (etfData.expense_ratio_raw) return etfData.expense_ratio_raw;
    
    const expenseStr = String(etfData.expense_ratio || '0');
    return parseFloat(expenseStr.replace('%', '')) || 0;
  };

  return (
    <div style={{ 
      maxWidth: '1400px', 
      margin: '0 auto', 
      padding: window.innerWidth < 768 ? '1rem' : '2rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        borderRadius: '0.75rem',
        padding: window.innerWidth < 768 ? '1.5rem 1rem' : '2rem',
        marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
      }}>
        <h1 style={{
          fontSize: window.innerWidth < 768 ? '1.5rem' : '2rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <Building2 size={window.innerWidth < 768 ? 24 : 32} />
          ETF X-Ray™
        </h1>
        <p style={{ 
          fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem', 
          opacity: 0.9 
        }}>
          Deep analysis of ETF holdings, sectors, expenses, and hidden overlaps
        </p>
      </div>

      {/* ✅ MOBILE-RESPONSIVE SEARCH BAR */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
        marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
      }}>
        <div style={{ 
          display: window.innerWidth < 640 ? 'flex' : 'grid',
          flexDirection: window.innerWidth < 640 ? 'column' : 'row',
          gridTemplateColumns: window.innerWidth >= 640 ? '1fr auto' : '1fr',
          gap: window.innerWidth < 640 ? '0.75rem' : '1rem',
          alignItems: 'start'
        }}>
          <div style={{ position: 'relative', width: '100%', overflow: 'visible' }}>
            <input
              type="text"
              value={searchSymbol}
              onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              onFocus={() => searchSymbol.length > 0 && setShowSuggestions(true)}
              placeholder="Enter ETF symbol (e.g., SPY, QQQ, VOO)"
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#0f172a',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            
            {/* ✅ AUTOCOMPLETE DROPDOWN */}
            {showSuggestions && suggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '0.5rem',
                background: '#1e293b',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
              }}>
                {suggestions.map((etf, idx) => (
                  <div
                    key={idx}
                    onClick={() => selectSuggestion(etf)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      borderBottom: idx < suggestions.length - 1 ? '1px solid #334155' : 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '600', color: '#60a5fa', fontSize: '0.875rem' }}>
                      {etf.symbol}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                      {etf.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* ✅ RESPONSIVE BUTTON */}
          <button
            onClick={() => searchETF()}
            disabled={loading || !searchSymbol.trim()}
            style={{
              width: window.innerWidth < 640 ? '100%' : '170px',
              height: '48px',
              background: loading ? '#475569' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            {loading ? (
              <>
                <Loader size={18} className="spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Search size={18} />
                Analyze ETF
              </>
            )}
          </button>
        </div>

        {/* ✅ QUICK POPULAR ETFs */}
        {!etfData && !loading && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Popular ETFs:
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['SPY', 'QQQ', 'VOO', 'VTI', 'AGG', 'GLD'].map(sym => (
                <button
                  key={sym}
                  onClick={() => {
                    setSearchSymbol(sym);
                    searchETF(sym);
                  }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '0.375rem',
                    color: '#c4b5fd',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
                >
                  {sym}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            color: '#fca5a5',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={18} />
            {error}
          </div>
        )}
      </div>

      {/* ETF Data Display */}
      {etfData && (
        <div>
          {/* Overview Cards - ✅ FIXED: No duplicate % signs */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 640 ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: window.innerWidth < 768 ? '0.75rem' : '1rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
          }}>
            <MetricCard
              icon={<Building2 size={20} color="#60a5fa" />}
              label="Fund Name"
              value={etfData.name || symbol}
              color="#60a5fa"
            />
            <MetricCard
              icon={<DollarSign size={20} color="#10b981" />}
              label="Expense Ratio"
              value={formatDisplayValue(etfData.expense_ratio)}
              color="#10b981"
            />
            <MetricCard
              icon={<Percent size={20} color="#8b5cf6" />}
              label="Dividend Yield"
              value={formatDisplayValue(etfData.dividend_yield)}
              color="#8b5cf6"
            />
            <MetricCard
              icon={<TrendingUp size={20} color="#f59e0b" />}
              label="AUM"
              value={formatAUM(etfData.aum || etfData.net_assets)}
              color="#f59e0b"
            />
          </div>

          {/* Holdings Breakdown */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem',
            overflowX: 'auto'
          }}>
            <h3 style={{
              fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Award size={window.innerWidth < 768 ? 20 : 24} color="#60a5fa" />
              Top 10 Holdings
            </h3>
            <HoldingsTable holdings={etfData.holdings || []} />
          </div>

          {/* Sector Allocation - Only show if we have sector data */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: window.innerWidth < 768 ? '1rem' : '2rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
          }}>
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: window.innerWidth < 768 ? '1rem' : '1.5rem'
            }}>
              <h3 style={{
                fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
                fontWeight: '600',
                marginBottom: '1.5rem'
              }}>
                📊 Sector Allocation
              </h3>
              <SectorChart data={etfData.sector_weights || {}} colors={SECTOR_COLORS} />
            </div>

            {/* Geographic Exposure - Show message if no data */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: window.innerWidth < 768 ? '1rem' : '1.5rem'
            }}>
              <h3 style={{
                fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
                fontWeight: '600',
                marginBottom: '1.5rem'
              }}>
                🌍 Geographic Exposure
              </h3>
              <GeographicChart data={etfData.country_weights || {}} colors={GEO_COLORS} symbol={symbol} />
            </div>
          </div>

          {/* Expense Analysis */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
          }}>
            <h3 style={{
              fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <DollarSign size={window.innerWidth < 768 ? 20 : 24} color="#10b981" />
              Cost Impact Analysis
            </h3>
            <ExpenseImpact expenseRatio={getExpenseRatioRaw()} />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPONENT: Metric Card
// ============================================
const MetricCard = ({ icon, label, value, color }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '1.25rem'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.75rem'
    }}>
      {icon}
      <span style={{
        fontSize: '0.75rem',
        color: '#94a3b8',
        textTransform: 'uppercase',
        fontWeight: '600'
      }}>
        {label}
      </span>
    </div>
    <div style={{
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: color || '#e2e8f0'
    }}>
      {value}
    </div>
  </div>
);

// ============================================
// COMPONENT: Holdings Table
// ============================================
const HoldingsTable = ({ holdings }) => {
  if (!holdings || holdings.length === 0) {
    return <p style={{ color: '#94a3b8', textAlign: 'center' }}>No holdings data available</p>;
  }

  const top10 = Array.isArray(holdings) ? holdings.slice(0, 10) : [];

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse',
        minWidth: window.innerWidth < 768 ? '500px' : 'auto'
      }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #334155' }}>
            <th style={tableHeaderStyle}>Ticker</th>
            <th style={tableHeaderStyle}>Company</th>
            <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Weight</th>
          </tr>
        </thead>
        <tbody>
          {top10.map((holding, idx) => {
            // ✅ SAFELY PARSE WEIGHT - handle both number and string with %
            let weightDisplay = 'N/A';
            if (holding.weight !== undefined && holding.weight !== null) {
              const weightStr = String(holding.weight);
              const weightNum = parseFloat(weightStr.replace('%', ''));
              if (!isNaN(weightNum)) {
                weightDisplay = `${weightNum.toFixed(2)}%`;
              }
            }
            
            return (
              <tr key={idx} style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ ...tableCellStyle, fontWeight: '700', color: '#60a5fa' }}>
                  {holding.ticker || holding.symbol || 'N/A'}
                </td>
                <td style={tableCellStyle}>
                  {holding.name || holding.description || holding.ticker || 'N/A'}
                </td>
                <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: '600' }}>
                  {weightDisplay}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================
// COMPONENT: Sector Chart
// ============================================
const SectorChart = ({ data, colors }) => {
  const chartData = Object.keys(data).map((sector, idx) => ({
    name: sector,
    value: parseFloat(data[sector]) || 0,
    fill: colors[idx % colors.length]
  }));

  if (chartData.length === 0) {
    return <p style={{ color: '#94a3b8', textAlign: 'center' }}>No sector data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={100}
          label={(entry) => `${entry.name}: ${entry.value.toFixed(1)}%`}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// ============================================
// COMPONENT: Geographic Chart
// ✅ IMPROVED: Show helpful message when no data
// ============================================
const GeographicChart = ({ data, colors, symbol }) => {
  const chartData = Object.keys(data).map((country, idx) => ({
    name: country,
    value: parseFloat(data[country]) || 0,
    fill: colors[idx % colors.length]
  }));

  if (chartData.length === 0) {
    // ✅ Show helpful message instead of just "no data"
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#94a3b8'
      }}>
        <Globe size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
        <p style={{ marginBottom: '0.5rem' }}>Geographic data not available</p>
        <p style={{ fontSize: '0.75rem', color: '#64748b' }}>
          Most US-focused ETFs like {symbol || 'this ETF'} primarily hold domestic securities.
          <br />
          For international exposure details, check the fund's prospectus.
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey="name" stroke="#94a3b8" />
        <YAxis stroke="#94a3b8" />
        <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
        <Bar dataKey="value" fill="#8b5cf6" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ============================================
// COMPONENT: Expense Impact
// ============================================
const ExpenseImpact = ({ expenseRatio }) => {
  const calculateImpact = (amount, years, ratio) => {
    // Calculate how much the expense ratio costs over time
    // Assuming 7% annual return
    const annualReturn = 0.07;
    const withoutFees = amount * Math.pow(1 + annualReturn, years);
    const withFees = amount * Math.pow(1 + annualReturn - (ratio / 100), years);
    return (withoutFees - withFees).toFixed(0);
  };

  const scenarios = [
    { amount: 10000, years: 10, label: '$10,000 over 10 years' },
    { amount: 50000, years: 20, label: '$50,000 over 20 years' },
    { amount: 100000, years: 30, label: '$100,000 over 30 years' }
  ];

  return (
    <div>
      <p style={{ color: '#cbd5e1', marginBottom: '1.5rem' }}>
        Annual expense ratio of <strong style={{ color: '#fbbf24' }}>{expenseRatio}%</strong> will cost you:
      </p>
      <div style={{ display: 'grid', gap: '1rem' }}>
        {scenarios.map((scenario, idx) => (
          <div key={idx} style={{
            padding: '1rem',
            background: 'rgba(15, 23, 42, 0.6)',
            borderRadius: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#cbd5e1' }}>{scenario.label}</span>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444' }}>
              ${parseInt(calculateImpact(scenario.amount, scenario.years, expenseRatio)).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: 'rgba(96, 165, 250, 0.1)',
        border: '1px solid rgba(96, 165, 250, 0.3)',
        borderRadius: '0.5rem'
      }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#93c5fd' }}>
          💡 <strong>Tip:</strong> Lower expense ratios compound over time. Even 0.10% difference can save thousands!
        </p>
      </div>
    </div>
  );
};

// ============================================
// HELPER STYLES
// ============================================
const tableHeaderStyle = {
  padding: '0.75rem',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#94a3b8',
  textTransform: 'uppercase',
  textAlign: 'left'
};

const tableCellStyle = {
  padding: '0.75rem',
  fontSize: '0.875rem',
  color: '#cbd5e1'
};

export default ETFXRay;